import { useEffect, useState } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import AdminPaywallBanner from "@/components/AdminPaywallBanner";
import { useIsAdmin } from "@/lib/auth";
import { RecruitingGate, isRecruitingRequired } from "@/components/Pricing";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, Sparkles, Shield, FileText, Search, ShieldCheck } from "lucide-react";

const SPORTS = ["Lacrosse"];  // Other sports coming soon — Lacrosse-only at launch
const GENDERS = ["Men", "Women", "Coed"];
const LEVELS = ["MS", "HS", "JUCO", "College"];
const DIVISIONS = ["D1", "D2", "D3", "NAIA", "NJCAA", "MCLA D1", "MCLA D2", "WCLA", "NCLA", "Club", "HS Varsity", "JV", "Freshman", "Middle School"];
const ROLES = ["Head Coach", "Associate Head Coach", "Assistant Coach", "Offensive Coordinator", "Defensive Coordinator", "Director of Operations"];
const REGIONS = ["Northeast", "Southeast", "Upper Midwest", "Lower Midwest", "Northwest", "Southwest"];
const SETTINGS = ["Public", "Private"];

export default function StealthSearch() {
  const isAdmin = useIsAdmin();
  const [gated, setGated] = useState(false);
  const [criteria, setCriteria] = useState({
    sport: "Lacrosse", gender: "Men", level: "College", division: "D1",
    role: "Head Coach", region: "Northeast", setting: "Private", description: "",
  });
  const [history, setHistory] = useState([]);
  const [activeSearch, setActiveSearch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nda, setNda] = useState(null);
  const [showNda, setShowNda] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/stealth/searches").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/stealth/nda").then(({ data }) => setNda(data)).catch(() => {});
  }, []);

  const u = (k, v) => setCriteria({ ...criteria, [k]: v });

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stealth/searches", criteria);
      setActiveSearch(data);
      setHistory((h) => [data, ...h]);
      toast.success(`Preview ready — ${data.total} candidates matched.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stealth search failed");
    } finally { setBusy(false); }
  };

  const openSearch = async (id) => {
    try {
      const { data } = await api.get(`/stealth/searches/${id}`);
      setActiveSearch(data);
    } catch (e) {
      toast.error("Could not load that search");
    }
  };

  const acceptNda = async () => {
    if (!activeSearch || !typedName.trim()) { toast.error("Type your full legal name to accept"); return; }
    setAccepting(true);
    try {
      await api.post("/stealth/nda/accept", { search_id: activeSearch.id, typed_name: typedName, accept: true });
      toast.success("NDA accepted. You can now reveal identities.");
      setShowNda(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "NDA acceptance failed");
    } finally { setAccepting(false); }
  };

  const payAndReveal = async (tier) => {
    if (!activeSearch) return;
    setPaying(true);
    try {
      await api.post("/stealth/searches/reveal", { search_id: activeSearch.id, tier });
      const { data } = await api.get(`/stealth/searches/${activeSearch.id}`);
      setActiveSearch(data);
      toast.success("Identities revealed — included in Program Recruiting.");
    } catch (e) {
      if (isRecruitingRequired(e)) setGated(true);
      else toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Reveal failed");
    } finally {
      setPaying(false);
    }
  };

  // Admin-only: skip NDA + Stripe and reveal everyone in this search for free.
  const adminReveal = async () => {
    if (!activeSearch) return;
    setPaying(true);
    try {
      await api.post("/stealth/searches/reveal", { search_id: activeSearch.id, tier: "full" });
      const { data } = await api.get(`/stealth/searches/${activeSearch.id}`);
      setActiveSearch(data);
      toast.success("Admin reveal complete — identities unlocked.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Admin reveal failed");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-6xl" data-testid="stealth-search-page">
        <div className="flex items-center gap-2 text-blaze text-xs tracking-widest"><Shield size={14} /> STEALTH SEARCH</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">SEARCH WITHOUT TIPPING YOUR HAND</h1>
        <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
          Free preview shows you the match count and quality distribution — no names, no current employers.
          Identity reveals are included in Program Recruiting, always NDA-gated.
        </p>

        {/* How-it-works panel */}
        <div className="mt-6 border border-volt/30 bg-[#0A101A] p-5" data-testid="stealth-how-it-works">
          <div className="flex items-center gap-2 text-volt text-[10px] tracking-[0.3em] mb-4">
            <ShieldCheck size={14} /> HOW STEALTH SEARCH WORKS
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm text-neutral-300">
            <li className="flex gap-3" data-testid="stealth-step-1">
              <span className="text-mono text-blaze text-xs mt-0.5">01</span>
              <div>
                <b className="text-white">Set your criteria below.</b> Sport, gender, level, division,
                role, region, setting. Add a plain-English note if you have a specific philosophy or
                context in mind — the MatchEngine weights it. <b className="text-volt">No candidate ever
                sees you searched.</b>
              </div>
            </li>
            <li className="flex gap-3" data-testid="stealth-step-2">
              <span className="text-mono text-blaze text-xs mt-0.5">02</span>
              <div>
                <b className="text-white">Free preview appears instantly.</b> You see total match count
                plus a tier histogram (Elite · Advanced · Solid · Emerging) — no names, no schools, no
                photos. Enough to know whether a real search is worth it.
              </div>
            </li>
            <li className="flex gap-3" data-testid="stealth-step-3">
              <span className="text-mono text-blaze text-xs mt-0.5">03</span>
              <div>
                <b className="text-white">Sign the NDA to unlock identities.</b> One time, per search.
                Locks you in to CoachConnect&apos;s confidentiality terms. Nothing hits your school&apos;s
                inbox — the reveal happens only inside this dashboard.
              </div>
            </li>
            <li className="flex gap-3" data-testid="stealth-step-4">
              <span className="text-mono text-blaze text-xs mt-0.5">04</span>
              <div>
                <b className="text-white">Reveal with Program Recruiting.</b> Identity reveals are included in
                Program Recruiting ($5/month per program) — names, current programs, CIR scores, match rationale,
                always behind the NDA.
              </div>
            </li>
            <li className="flex gap-3 md:col-span-2" data-testid="stealth-step-5">
              <span className="text-mono text-blaze text-xs mt-0.5">05</span>
              <div>
                <b className="text-white">Reach out on your timeline.</b> When you engage a coach, we
                introduce you anonymously first — they see match confidence + your criteria, not your
                program name. Your identity only surfaces if <em>you</em> choose to reveal it.
              </div>
            </li>
          </ol>
          <div className="mt-4 pt-3 border-t border-neutral-800 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-neutral-500">
            <span className="flex items-center gap-1"><Lock size={11} className="text-volt" /> Searches saved to your dashboard only</span>
            <span className="flex items-center gap-1"><Shield size={11} className="text-volt" /> Duplicate criteria reuse the same search — no clutter</span>
            <span className="flex items-center gap-1"><Sparkles size={11} className="text-volt" /> Preview refreshes as the TKG ingests new coaches</span>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Criteria */}
          <div className="lg:col-span-5 border border-neutral-800 bg-appcard p-6" data-testid="stealth-criteria">
            <div className="text-xs tracking-widest text-volt mb-4">CRITERIA</div>
            <div className="space-y-3">
              <Pair><Sel label="Sport" value={criteria.sport} opts={SPORTS} onChange={(v) => u("sport", v)} testid="stealth-sport" /><Sel label="Gender" value={criteria.gender} opts={GENDERS} onChange={(v) => u("gender", v)} testid="stealth-gender" /></Pair>
              <Pair><Sel label="Level" value={criteria.level} opts={LEVELS} onChange={(v) => u("level", v)} testid="stealth-level" /><Sel label="Division" value={criteria.division || ""} opts={["", ...DIVISIONS]} onChange={(v) => u("division", v)} testid="stealth-division" /></Pair>
              <Pair><Sel label="Role" value={criteria.role} opts={ROLES} onChange={(v) => u("role", v)} testid="stealth-role" /><Sel label="Region" value={criteria.region || ""} opts={["", ...REGIONS]} onChange={(v) => u("region", v)} testid="stealth-region" /></Pair>
              <Sel label="Setting" value={criteria.setting || ""} opts={["", ...SETTINGS]} onChange={(v) => u("setting", v)} testid="stealth-setting" />
              <Field label="Commitment">
                <select className="input" data-testid="stealth-position-type" value={criteria.position_type || ""} onChange={(e) => u("position_type", e.target.value || null)}>
                  <option value="">Any commitment</option>
                  <option value="part_time">Part-time (stipend) — only coaches open to it</option>
                  <option value="full_time">Full-time — only coaches open to it</option>
                </select>
              </Field>
              <Field label="Description / notes (optional)">
                <textarea className="input min-h-[80px]" data-testid="stealth-description" value={criteria.description} onChange={(e) => u("description", e.target.value)} placeholder="e.g. rebuilding D1 program, value defensive-minded HCs with HBCU pipeline strength." />
              </Field>
            </div>
            <button
              onClick={run}
              disabled={busy}
              data-testid="run-stealth-btn"
              className="mt-5 w-full bg-blaze text-white heading text-lg py-3 min-h-[52px] hover:glow-blaze transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Search size={16} /> {busy ? "MATCHING…" : "RUN STEALTH PREVIEW (FREE)"}
            </button>
            <div className="mt-3 text-[11px] text-neutral-500">Preview — no NDA, identities never shown. Stealth search is part of Program Recruiting.</div>
          </div>

          {/* Active result */}
          <div className="lg:col-span-7 space-y-6">
            {!activeSearch && (
              <div className="border border-dashed border-neutral-800 p-8 text-neutral-500 text-sm">Run a preview to see the match distribution. Identities remain locked until you pay.</div>
            )}
            {activeSearch && (
              <div className="border border-volt p-6 bg-[#0D0D14]" data-testid="stealth-active-card">
                <div className="text-xs tracking-widest text-volt">STEALTH RESULT · {activeSearch.id?.slice(0, 8)}</div>
                <div className="mt-2 grid grid-cols-3 gap-3">
                  <Stat label="Total matches" value={activeSearch.total} testid="stat-total" />
                  <Stat label="90%+ fit" value={activeSearch.histogram?.["90_plus"] ?? 0} accent="blaze" testid="stat-90" />
                  <Stat label="75-90% fit" value={activeSearch.histogram?.["75_90"] ?? 0} accent="volt" testid="stat-75" />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <Stat label="65-75% fit" value={activeSearch.histogram?.["65_75"] ?? 0} testid="stat-65" />
                  {Object.entries(activeSearch.tier_counts || {}).slice(0, 2).map(([k, v]) => (
                    <Stat key={k} label={`Tier · ${k}`} value={v} testid={`stat-tier-${k.replace(/\s+/g, "-").toLowerCase()}`} />
                  ))}
                </div>

                {activeSearch.reveal_status === "preview" && (
                  <div className="mt-6 border-t border-neutral-800 pt-5">
                    {isAdmin ? (
                      <div className="space-y-4">
                        <AdminPaywallBanner
                          feature="Stealth Search full reveal"
                          note="One-click reveal — no NDA modal, no Stripe charge."
                        />
                        <button
                          data-testid="admin-reveal-btn"
                          onClick={adminReveal}
                          disabled={paying}
                          className="min-h-[44px] px-5 bg-volt text-white heading text-sm tracking-widest hover:glow-volt transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                          <ShieldCheck size={14} /> {paying ? "REVEALING…" : "ADMIN · REVEAL ALL (FREE)"}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-blaze text-xs tracking-widest"><Lock size={12} /> IDENTITIES LOCKED</div>
                        <p className="text-sm text-neutral-300 mt-2">Sign the NDA and pay a one-time fee to reveal candidates.</p>
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button data-testid="open-nda-btn" onClick={() => setShowNda(true)} className="min-h-[44px] px-4 border border-volt text-volt heading text-sm tracking-widest hover:bg-volt hover:text-white transition-colors flex items-center gap-2"><FileText size={14} /> READ & SIGN NDA</button>
                          <button data-testid="pay-shortlist-btn" onClick={() => payAndReveal("shortlist")} disabled={paying} className="min-h-[44px] px-4 bg-blaze text-white heading text-sm tracking-widest hover:glow-blaze transition-all flex items-center gap-2 disabled:opacity-50"><Eye size={14} /> REVEAL TOP 5 · $750</button>
                          <button data-testid="pay-full-btn" onClick={() => payAndReveal("full")} disabled={paying} className="min-h-[44px] px-4 border border-blaze text-blaze heading text-sm tracking-widest hover:bg-blaze hover:text-white transition-all flex items-center gap-2 disabled:opacity-50"><Sparkles size={14} /> FULL REVEAL · $1,750</button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {activeSearch.reveal_status !== "preview" && activeSearch.candidates?.length > 0 && (
                  <div className="mt-6 border-t border-neutral-800 pt-5">
                    <div className="flex items-center gap-2 text-volt text-xs tracking-widest"><Eye size={12} /> REVEALED · CONFIDENTIAL UNDER NDA</div>
                    <div className="mt-3 space-y-2" data-testid="revealed-candidates">
                      {activeSearch.candidates.map((c) => {
                        // When the search specified a division (HS/D3/D2/D1) the backend
                        // emits a target-aware CIR — use that score so a sitting D1 HC
                        // doesn't get displayed at their base ranking inside a D1 search.
                        const cir = c.cir_target || c.qbr;
                        const targetLevel = c.cir_target?.target_level;
                        return (
                          <div key={c.id} data-testid={`revealed-${c.id}`} className="border border-neutral-800 bg-appbg p-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="heading text-lg truncate">{c.name || c.display_name}</div>
                              <div className="text-xs text-neutral-400 truncate">{c.headline}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="heading text-2xl text-blaze">{cir?.score}</div>
                              <div className="text-[10px] tracking-widest text-neutral-500">
                                {cir?.tier}
                                {targetLevel ? <span className="text-volt"> · {targetLevel} FIT</span> : null}
                                {" · "}{c.stealth_score}% MATCH
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* History */}
            <div className="border border-neutral-800 bg-appcard p-4">
              <div className="text-xs tracking-widest text-neutral-500 mb-2">YOUR STEALTH SEARCHES</div>
              {history.length === 0 && <div className="text-neutral-500 text-xs">No searches yet.</div>}
              <div className="space-y-1" data-testid="stealth-history">
                {history.map((h) => {
                  const c = h.criteria || {};
                  const primary = [c.division, c.gender, c.sport].filter(Boolean).join(" ");
                  const role = c.role || "";
                  const extras = [c.region, c.setting, c.salary_band].filter(Boolean).join(" · ");
                  return (
                    <button
                      key={h.id}
                      data-testid={`history-${h.id}`}
                      onClick={() => openSearch(h.id)}
                      className="w-full text-left px-3 py-2 text-sm border border-neutral-800 hover:border-volt"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-neutral-300 truncate">
                          {primary || "Any level"}{role ? ` · ${role}` : ""}
                        </span>
                        <span className="text-xs text-neutral-500 flex items-center gap-1 shrink-0">
                          {h.reveal_status === "preview" ? <EyeOff size={12} /> : <Eye size={12} />} {h.total}
                        </span>
                      </div>
                      {extras && (
                        <div className="text-[10px] text-neutral-500 truncate mt-0.5">{extras}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* NDA modal */}
        {showNda && nda && (
          <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center px-4" data-testid="nda-modal" onClick={() => setShowNda(false)}>
            <div className="bg-appcard border border-neutral-800 max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-neutral-800">
                <div className="flex items-center gap-2 text-blaze text-xs tracking-widest"><Shield size={14} /> STEALTH NDA · {nda.version}</div>
                <div className="heading text-2xl mt-1">CONFIDENTIALITY AGREEMENT</div>
              </div>
              <div className="p-5 overflow-y-auto text-sm text-neutral-300 whitespace-pre-wrap leading-relaxed" data-testid="nda-text">{nda.text}</div>
              <div className="p-5 border-t border-neutral-800 space-y-3">
                <input
                  className="w-full bg-appcard border border-neutral-800 px-3 py-3 text-sm outline-none focus:border-blaze min-h-[44px]"
                  data-testid="nda-typed-name"
                  placeholder="Type your full legal name to sign"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                />
                <div className="flex gap-2">
                  <button data-testid="nda-accept-btn" onClick={acceptNda} disabled={accepting} className="flex-1 min-h-[44px] bg-blaze text-white heading text-sm hover:glow-blaze disabled:opacity-50">
                    {accepting ? "SIGNING…" : "I ACCEPT THE NDA"}
                  </button>
                  <button data-testid="nda-cancel-btn" onClick={() => setShowNda(false)} className="min-h-[44px] px-4 border border-neutral-700 text-neutral-400 text-xs tracking-widest">CANCEL</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`.input { width: 100%; background: #121212; border: 1px solid #27272A; padding: 10px 12px; color: white; outline: none; font-size: 14px; min-height: 44px; }
      .input:focus { border-color: #FF3B30; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="text-[10px] tracking-widest text-neutral-500 mb-1.5">{label.toUpperCase()}</div>{children}</label>;
}
function Pair({ children }) { return <div className="grid grid-cols-2 gap-3">{children}</div>; }
function Sel({ label, value, opts, onChange, testid }) {
  return (
    <Field label={label}>
      <select className="input" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}>
        {opts.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </Field>
  );
}
function Stat({ label, value, accent, testid }) {
  return (
    <div className={`border ${accent === "blaze" ? "border-blaze" : accent === "volt" ? "border-volt" : "border-neutral-800"} p-3`} data-testid={testid}>
      <div className="text-[10px] tracking-widest text-neutral-500">{label}</div>
      <div className={`heading text-3xl ${accent === "blaze" ? "text-blaze" : accent === "volt" ? "text-volt" : ""}`}>{value}</div>
    </div>
  );
}
